import React, { useState, useEffect, useRef } from 'react';
import { useRBAC } from '../../../contexts/RBACContext';
import { useAuth } from '../../../contexts/AuthContext';
import PermissionGuard from '../../Common/PermissionGuard';
import CustomSelect from '../../Common/CustomSelect';
import TextInput from '../../Common/TextInput';
import RoleForm from './RoleForm';
import RolePermissions from './RolePermissions';
import {
  CardGridSkeleton,
  DataViewLoadingContainer,
  TableSkeleton,
} from '../Common/DataViewLoading';

const RoleManagement = () => {
  const {
    roles,
    selectedRole,
    loading,
    error,
    filters,
    pagination,
    fetchRoles,
    selectRole,
    deleteRole,
    setFilters,
    updatePagination,
    clearError,
    rbacUtils
  } = useRBAC();

  const { isOnlyAdmin, isSuperAdmin } = useAuth();
  const isAdminOnly = typeof isOnlyAdmin === 'function'
    ? isOnlyAdmin()
    : (typeof isSuperAdmin === 'function' ? !isSuperAdmin() : Boolean(isOnlyAdmin));

  // Get initial view from localStorage or default to 'card'
  const getInitialView = () => {
    try {
      return localStorage.getItem('roleManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  };

  const [view, setView] = useState(getInitialView()); // 'card', 'list', 'form'
  const [editingRole, setEditingRole] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [localLevelFilter, setLocalLevelFilter] = useState(null);
  const [localActiveFilter, setLocalActiveFilter] = useState(true);
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const [isMounted, setIsMounted] = useState(false);

  // Default filters for comparison
  const defaultFilters = {
    level: null,
    isActive: true,
    search: ''
  };

  // Save view preference to localStorage
  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('roleManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference:', error);
    }
  };

  // Reset filters once on mount; initialize pagination fully to avoid limit undefined→20 re-fetch
  useEffect(() => {
    setSearchInput('');
    setLocalLevelFilter(null);
    setLocalActiveFilter(true);
    setFilters(defaultFilters);
    updatePagination('roles', { current: 1, limit: 20, total: 0, pages: 0 });
    setIsMounted(true);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      setSearchInput('');
      setFilters(defaultFilters);
      setIsMounted(false);
      isInitialMount.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Single fetch effect — avoid the old mount + filter effects both calling fetchRoles
  useEffect(() => {
    if (!isMounted) return;
    fetchRoles();
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, filters.search, filters.level, filters.isActive, pagination.roles.current, pagination.roles.limit]);

  // Debounced search handler
  const handleSearch = (searchTerm) => {
    setSearchInput(searchTerm);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounce (500ms)
    searchTimeoutRef.current = setTimeout(() => {
      setFilters({ search: searchTerm });
      updatePagination('roles', { current: 1 });
    }, 500);
  };

  const handleLevelFilter = (level) => {
    setLocalLevelFilter(level);
    setFilters({ level: level });
    updatePagination('roles', { current: 1 });
  };

  const handleActiveFilter = (isActive) => {
    setLocalActiveFilter(isActive);
    setFilters({ isActive: isActive });
    updatePagination('roles', { current: 1 });
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setLocalLevelFilter(null);
    setLocalActiveFilter(true);
    setFilters(defaultFilters);
    updatePagination('roles', { current: 1 });
  };

  // Check if filters are modified from defaults
  const hasModifiedFilters = () => {
    return searchInput !== defaultFilters.search ||
           localLevelFilter !== defaultFilters.level ||
           localActiveFilter !== defaultFilters.isActive;
  };

  const handlePageChange = (newPage) => {
    updatePagination('roles', { current: newPage });
  };

  const handleLimitChange = (newLimit) => {
    updatePagination('roles', { current: 1, limit: newLimit });
  };

  const handleRoleSelect = (role) => {
    if (isAdminOnly && role?.code?.toUpperCase() === 'SUPER_ADMIN') {
      return;
    }
    selectRole(role);
    setView('permissions');
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setView('form');
  };

  const handleEditRole = (role) => {
    if (isAdminOnly && role?.code?.toUpperCase() === 'SUPER_ADMIN') {
      return;
    }
    setEditingRole(role);
    setView('form');
  };

  const handleDeleteRole = async (role) => {
    const result = await deleteRole(role._id);
    if (result.success) {
      setShowDeleteConfirm(null);
      fetchRoles();
    }
  };

  const handleFormSuccess = () => {
    const savedView = getInitialView();
    setView(savedView);
    setEditingRole(null);
    fetchRoles();
  };

  const handleBackToView = () => {
    const savedView = getInitialView();
    setView(savedView);
  };

  const sortedRoles = rbacUtils.sortRolesByHierarchy(roles).filter(r => r != null);
  // Hardcoded levels from 1 to 10
  const allLevels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const getRoleIcon = (level) => {
    if (level <= 2) return '👑'; // Crown for top levels
    if (level <= 4) return '⭐'; // Star for manager levels
    if (level <= 6) return '🔹'; // Diamond for mid levels
    return '👤'; // User for lower levels
  };

  const renderRoleRow = (role) => {
    if (!role) return null;
    return (
    <tr key={role._id} className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded flex items-center justify-center text-sm font-semibold ${role.level === 1 ? 'bg-red-100 text-red-600' :
              role.level === 2 ? 'bg-orange-100 text-orange-600' :
                role.level === 3 ? 'bg-yellow-100 text-yellow-600' :
                  role.level === 4 ? 'bg-green-100 text-green-600' :
                    role.level === 5 ? 'bg-blue-100 text-blue-600' :
                      role.level === 6 ? 'bg-indigo-100 text-indigo-600' :
                        'bg-gray-100 text-gray-600'
            }`}>
            {getRoleIcon(role.level)}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <p className="text-sm font-semibold text-gray-900">{role.name}</p>
              {role.isSystemRole && (
                <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full" title="System Role"></span>
              )}
            </div>
            <p className="text-xs text-gray-500 font-mono">{role.code}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${role.level === 1 ? 'bg-red-100 text-red-800' :
            role.level === 2 ? 'bg-orange-100 text-orange-800' :
              role.level === 3 ? 'bg-yellow-100 text-yellow-800' :
                role.level === 4 ? 'bg-green-100 text-green-800' :
                  role.level === 5 ? 'bg-blue-100 text-blue-800' :
                    role.level === 6 ? 'bg-indigo-100 text-indigo-800' :
                      'bg-gray-100 text-gray-800'
          }`}>
          Level {role.level}
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-700 line-clamp-2">{role.description || 'No description'}</p>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${role.isActive !== false
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
          }`}>
          {role.isActive !== false ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          {isAdminOnly && role.code?.toUpperCase() === 'SUPER_ADMIN' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Super Administrator role permissions are system-level and cannot be altered">
              <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Protected
            </span>
          ) : (
            <>
              <button
                onClick={() => handleRoleSelect(role)}
                className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                title="View details & manage permissions"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </button>

              <PermissionGuard permission="USERS:ROLES:MANAGE">
                <button
                  onClick={() => handleEditRole(role)}
                  className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                  title="Edit role"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>

                {!role.isSystemRole && !['JUDGE', 'MEMBER', 'EXTERNAL_COMPANY', 'STD_EMPLOYEE', 'SUPER_ADMIN'].includes(role.code?.toUpperCase()) && (
                  <button
                    onClick={() => setShowDeleteConfirm(role)}
                    className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                    title="Delete role"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </PermissionGuard>
            </>
          )}
        </div>
      </td>
    </tr>
    );
  };

  const renderRoleCard = (role) => {
    if (!role) return null;
    return (
      <div key={role._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-28 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0 ${role.level === 1 ? 'bg-red-100 text-red-600' :
                  role.level === 2 ? 'bg-orange-100 text-orange-600' :
                    role.level === 3 ? 'bg-yellow-100 text-yellow-600' :
                      role.level === 4 ? 'bg-green-100 text-green-600' :
                        role.level === 5 ? 'bg-blue-100 text-blue-600' :
                          role.level === 6 ? 'bg-indigo-100 text-indigo-600' :
                            'bg-gray-100 text-gray-600'
                }`}>
                {getRoleIcon(role.level)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-1">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {role.name}
                  </h3>
                  {role.isSystemRole && (
                    <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0" title="System Role"></span>
                  )}
                </div>
                <p className="text-xs text-gray-500 font-mono truncate">{role.code}</p>
              </div>
            </div>

            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              {isAdminOnly && role.code?.toUpperCase() === 'SUPER_ADMIN' ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Super Administrator role permissions are system-level and cannot be altered">
                  <svg className="w-2.5 h-2.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Protected
                </span>
              ) : (
                <>
                  <button
                    onClick={() => handleRoleSelect(role)}
                    className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                    title="View details & manage permissions"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </button>

                  <PermissionGuard permission="USERS:ROLES:MANAGE">
                    <button
                      onClick={() => handleEditRole(role)}
                      className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                      title="Edit role"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </PermissionGuard>

                  <PermissionGuard permission="USERS:ROLES:MANAGE">
                    {!role.isSystemRole && !['JUDGE', 'MEMBER', 'EXTERNAL_COMPANY', 'STD_EMPLOYEE', 'SUPER_ADMIN'].includes(role.code?.toUpperCase()) && (
                      <button
                        onClick={() => setShowDeleteConfirm(role)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                        title="Delete role"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </PermissionGuard>
                </>
              )}
            </div>
          </div>

          {role.description && (
            <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
              {role.description}
            </p>
          )}

          <div className="flex flex-wrap gap-1 mt-auto">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
              Level {role.level}
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              {role.permissionCount || 0} perms
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
              {role.userCount || 0} users
            </span>
            {role.isSystemRole && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                System
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const isRolesLoading = Boolean(loading.roles);
  const showRolesSkeleton = isRolesLoading && sortedRoles.length === 0;
  const showRolesOverlay = isRolesLoading && sortedRoles.length > 0;

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={clearError}
                className="ml-auto text-red-600 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {view === 'form' && (
          <RoleForm
            role={editingRole}
            onSuccess={handleFormSuccess}
            onCancel={handleBackToView}
          />
        )}

        {view === 'permissions' && selectedRole && (
          <RolePermissions
            role={selectedRole}
            onBack={handleBackToView}
          />
        )}

        {(view === 'card' || view === 'list') && (
          <div className="bg-white shadow-sm border border-gray-200">
            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0">
              <div className="flex flex-col gap-2 min-w-0">
                {/* Search */}
                <div className="w-full min-w-0">
                  <TextInput
                    variant="input"
                    type="text"
                    placeholder="Search roles by name, code, or description..."
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                    icon={({ className }) => (
                      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                    className="text-sm py-1.5 border border-gray-300 shadow-sm !rounded-sm"
                  />
                </div>

                {/* Filters and Actions */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  {/* Status Filter */}
                  <CustomSelect
                    value={localActiveFilter === true ? 'active' : localActiveFilter === false ? 'inactive' : 'all'}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleActiveFilter(value === 'all' ? undefined : value === 'active');
                    }}
                    options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ]}
                    placeholder=""
                    size="sm"
                    variant="default"
                    className="min-w-[120px] !rounded-sm"
                  />

                  {/* Level Filter */}
                  <CustomSelect
                    value={localLevelFilter || ''}
                    onChange={(e) => handleLevelFilter(e.target.value ? parseInt(e.target.value) : null)}
                    options={[
                      { value: '', label: 'All Levels' },
                      ...allLevels.map(level => ({ value: level, label: `Level ${level}` }))
                    ]}
                    placeholder=""
                    size="sm"
                    variant="default"
                    className="min-w-[120px] !rounded-sm"
                  />

                  {/* Reset Filters Button */}
                  {hasModifiedFilters() && (
                    <button
                      onClick={handleResetFilters}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      title="Reset filters"
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset
                    </button>
                  )}

                  {/* View Toggle */}
                  <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                    <button
                      onClick={() => handleViewChange('card')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="Card view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                      </svg>
                      Cards
                    </button>
                    <button
                      onClick={() => handleViewChange('list')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="List view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      List
                    </button>
                  </div>

                  {/* Add Role Button */}
                  <PermissionGuard permission="USERS:ROLES:MANAGE">
                    <button
                      onClick={handleCreateRole}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Role
                    </button>
                  </PermissionGuard>
                </div>
              </div>
            </div>

            {/* Modern Pagination Section */}
            {pagination.roles && (
              <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                  {/* Results Info */}
                  <div className="flex items-center gap-2">
                    {/* Results Summary */}
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div className="text-sm flex items-center gap-2">
                        <div>
                          <span className="font-semibold text-gray-900">
                            {((pagination.roles.current - 1) * pagination.roles.limit) + 1}-{Math.min(pagination.roles.current * pagination.roles.limit, pagination.roles.total)}
                          </span>
                          <span className="text-gray-500 mx-1">of</span>
                          <span className="font-semibold text-gray-900">{pagination.roles.total}</span>
                          <span className="text-gray-500 ml-1">roles</span>
                        </div>
                        {loading.roles && roles.length > 0 && (
                          <div className="flex items-center">
                            <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center gap-3">
                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium">Show:</span>
                      <CustomSelect
                        id="limit"
                        value={pagination.roles.limit}
                        onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                        disabled={loading.roles}
                        options={[
                          { value: 10, label: '10' },
                          { value: 20, label: '20' },
                          { value: 50, label: '50' },
                          { value: 100, label: '100' }
                        ]}
                        size="sm"
                        variant="minimal"
                        className="min-w-[70px]"
                      />
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex items-center gap-1">
                      {/* First Page */}
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.roles.current <= 1 || loading.roles}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="First page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </button>

                      {/* Previous Page */}
                      <button
                        onClick={() => handlePageChange(Math.max(1, pagination.roles.current - 1))}
                        disabled={pagination.roles.current <= 1 || loading.roles}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="hidden sm:inline">Previous</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="hidden md:flex items-center gap-1">
                        {Array.from({ length: Math.min(5, pagination.roles.pages) }, (_, i) => {
                          let pageNum;
                          if (pagination.roles.pages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.roles.current <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.roles.current >= pagination.roles.pages - 2) {
                            pageNum = pagination.roles.pages - 4 + i;
                          } else {
                            pageNum = pagination.roles.current - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              disabled={loading.roles}
                              className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pageNum === pagination.roles.current
                                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                                  : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Page */}
                      <button
                        onClick={() => handlePageChange(Math.min(pagination.roles.pages, pagination.roles.current + 1))}
                        disabled={pagination.roles.current >= pagination.roles.pages || loading.roles}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      {/* Last Page */}
                      <button
                        onClick={() => handlePageChange(pagination.roles.pages)}
                        disabled={pagination.roles.current >= pagination.roles.pages || loading.roles}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="Last page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile Page Numbers */}
                <div className="flex md:hidden items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-200">
                  {Array.from({ length: Math.min(7, pagination.roles.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.roles.pages <= 7) {
                      pageNum = i + 1;
                    } else if (pagination.roles.current <= 4) {
                      pageNum = i + 1;
                    } else if (pagination.roles.current >= pagination.roles.pages - 3) {
                      pageNum = pagination.roles.pages - 6 + i;
                    } else {
                      pageNum = pagination.roles.current - 3 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading.roles}
                        className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pageNum === pagination.roles.current
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                            : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Role Grid */}
            <div className="p-2">
              {showRolesSkeleton ? (
                view === 'list' ? (
                  <TableSkeleton
                    columns={5}
                    rows={Math.min(pagination?.roles?.limit || 20, 10)}
                    columnWidths={['w-28', 'w-16', 'w-40', 'w-14', 'w-16']}
                  />
                ) : (
                  <CardGridSkeleton count={8} />
                )
              ) : sortedRoles.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No roles found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {filters.search || filters.level || filters.isActive === false ?
                      'Try adjusting your search or filter criteria.' :
                      'Get started by creating your first role.'
                    }
                  </p>
                  {(!filters.search && !filters.level && filters.isActive !== false) && (
                    <div className="mt-6">
                      <PermissionGuard permission="USERS:ROLES:MANAGE">
                        <button
                          onClick={handleCreateRole}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Create Role
                        </button>
                      </PermissionGuard>
                    </div>
                  )}
                </div>
              ) : (
                <DataViewLoadingContainer
                  loading={showRolesOverlay}
                  message={view === 'list' ? 'Loading roles…' : 'Loading…'}
                >
                  {/* Card View */}
                  {view === 'card' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {sortedRoles.map(renderRoleCard)}
                    </div>
                  )}

                  {/* List View */}
                  {view === 'list' && (
                    <div className="overflow-x-auto max-h-[70vh]">
                      <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Level
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedRoles.map(renderRoleRow)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </DataViewLoadingContainer>
              )}
            </div>

          </div>
        )}

        {/* Bottom Pagination */}
        {pagination.roles && (
          <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-t border-gray-200 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">{((pagination.roles.current - 1) * pagination.roles.limit) + 1}-{Math.min(pagination.roles.current * pagination.roles.limit, pagination.roles.total)}</span>
                  <span className="text-gray-500 mx-1">of</span>
                  <span className="font-semibold text-gray-900">{pagination.roles.total}</span>
                  <span className="text-gray-500 ml-1">roles</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handlePageChange(1)} disabled={pagination.roles.current <= 1 || loading.roles} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all" title="First page">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                </button>
                <button onClick={() => handlePageChange(Math.max(1, pagination.roles.current - 1))} disabled={pagination.roles.current <= 1 || loading.roles} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <div className="hidden md:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.roles.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.roles.pages <= 5) pageNum = i + 1;
                    else if (pagination.roles.current <= 3) pageNum = i + 1;
                    else if (pagination.roles.current >= pagination.roles.pages - 2) pageNum = pagination.roles.pages - 4 + i;
                    else pageNum = pagination.roles.current - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => handlePageChange(pageNum)} disabled={loading.roles} className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pageNum === pagination.roles.current ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'}`}>{pageNum}</button>
                    );
                  })}
                </div>
                <button onClick={() => handlePageChange(Math.min(pagination.roles.pages, pagination.roles.current + 1))} disabled={pagination.roles.current >= pagination.roles.pages || loading.roles} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm">
                  <span className="hidden sm:inline">Next</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
                <button onClick={() => handlePageChange(pagination.roles.pages)} disabled={pagination.roles.current >= pagination.roles.pages || loading.roles} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all" title="Last page">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modern Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Role</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone and may affect users assigned to this role.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteRole(showDeleteConfirm)}
                    className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
                  >
                    Delete Role
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;
